import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "types/store";
import { Button } from "aible";
import * as nextReducer from "containers/Next/NextReducer";
import * as chatAppReducer from "containers/ChatApp/reducer";
import * as homeReducer from "containers/Home/reducer";
import styles from "./style.module.css";
import { isEnabled } from "@/utils/featureFlags";
import LabledToggle from "components/Inputs/Toggle/Toggle";
import DatasetKPISettings from "components/KPIAnalysis/DatasetKPISettings";
import Tooltip from "components/Inputs/Tooltip";
export const KPIAnalysisSetup = ({ setIsKPIAnalysisReady, senseDatasetId }) => {
    const dispatch = useDispatch();
    const currentNextId = useSelector((state) => nextReducer.getCurrentNextId(state));
    const nextId = senseDatasetId ? "DATASET_" + senseDatasetId : currentNextId;
    const datasetId = useSelector((state) => senseDatasetId ?? nextReducer.getDatasetId(state, nextId));
    const isKPIAnalysis = useSelector((state) => chatAppReducer.getIsKPIAnalysis(state, senseDatasetId));
    const [hasChangedKPISettings, setHasChangedKPISettings] = useState(false);
    const [isReindexing, setIsReindexing] = useState(false);
    const dataset = useSelector((state) => homeReducer.getDataset(state, datasetId));
    const isProcessing = dataset?.status_name !== "DONE";
    const isError = dataset?.dataset_status === "ERROR";
    const isLoading = isReindexing || (isProcessing && !isError);
    const comparisonOptions = useSelector((state) => nextReducer.getComparisonOptions(state, nextId));
    const dateField = comparisonOptions?.dateField;
    const dateColumn = useSelector((state) => homeReducer.getKPIAnalysisDateColumn(state, datasetId));
    const timeBucket = useSelector((state) => homeReducer.getKPIAnalysisTimeBucket(state, datasetId));
    const KPISegment = useSelector((state) => homeReducer.getKPIAnalysisSegment(state, datasetId));
    const KPIDateRange = useSelector((state) => homeReducer.getKPIDateRange(state, datasetId));
    const hasAllInput = dateColumn !== undefined && timeBucket !== undefined && KPISegment !== undefined;
    useEffect(() => {
        if (isKPIAnalysis && dateField) {
            dispatch(homeReducer.updateKPIAnalysisDateColumn(datasetId, dateField));
        }
    }, [dateField]);
    useEffect(() => {
        if (!senseDatasetId) {
            if (dateField) {
                dispatch(homeReducer.updateKPIAnalysisDateColumn(datasetId, dateField));
            }
            dispatch(chatAppReducer.updateIsKPIAnalysis(true));
        }
        return () => {
            if (!senseDatasetId) {
                dispatch(chatAppReducer.updateIsKPIAnalysis(undefined));
            }
        };
    }, [datasetId]);
    let hasChanged = hasChangedKPISettings;
    if (comparisonOptions?.dateField !== dateColumn) {
        hasChanged = true;
    }
    const DATE_KEYS = ["currentStart", "currentEnd", "referenceStart", "referenceEnd"];
    DATE_KEYS.forEach((k) => {
        if (comparisonOptions?.[k] !== KPIDateRange?.[k]) {
            hasChanged = true;
        }
    });
    const handleSaveKPISettings = () => {
        dispatch(homeReducer.updateIsKPIAnalaysis(datasetId, isKPIAnalysis));
        if (comparisonOptions?.dateField && comparisonOptions?.dateField !== dateColumn) {
            dispatch(homeReducer.updateKPIAnalysisDateColumn(datasetId, comparisonOptions?.dateField));
        }
        const DATE_KEYS = ["currentStart", "currentEnd", "referenceStart", "referenceEnd"];
        DATE_KEYS.forEach((k) => {
            if (comparisonOptions?.[k] !== KPIDateRange?.[k]) {
                dispatch(homeReducer.updateKPIDateRangeValue(datasetId, k, comparisonOptions?.[k]));
            }
        });
        return dispatch(homeReducer.saveKPISettings(datasetId)).finally(() => {
            setHasChangedKPISettings(false);
        });
    };
    const isSaveDisabled = isKPIAnalysis && !KPISegment;
    return (React.createElement("div", { className: styles.KPIAnalysisSetupWrapper },
        isEnabled("document-set-kpi-analysis") && senseDatasetId && (React.createElement("div", { className: styles.KPIAnalysisSetupItem },
            React.createElement("div", { className: styles.KPIAnalysisSetupLabel, style: { marginRight: "8px" } },
                React.createElement("span", { style: { fontWeight: "bold", marginRight: "10px" } }, "What's Changed Overview:"),
                "Would you like to analyze the drivers of KPIs changes?"),
            React.createElement(Tooltip, { trigger: datasetId === undefined ? ["hover"] : [], overlay: "Select a dataset before enabling this setting" },
                React.createElement(LabledToggle, { style: { display: "block" }, checked: isKPIAnalysis, onChange: () => {
                        if (!isKPIAnalysis && dateField) {
                            dispatch(homeReducer.updateKPIAnalysisDateColumn(datasetId, dateField));
                        }
                        dispatch(homeReducer.updateIsKPIAnalaysis(senseDatasetId, !isKPIAnalysis));
                    }, disabled: datasetId === undefined, isAibleColors: true })))),
        isKPIAnalysis && (React.createElement("div", { onClick: () => {
                setHasChangedKPISettings(true);
                if (setIsKPIAnalysisReady) {
                    setIsKPIAnalysisReady(false);
                }
            } },
            React.createElement(DatasetKPISettings, { hideActions: true, datasetId: datasetId, isSense: Boolean(senseDatasetId) }))),
        !senseDatasetId && isKPIAnalysis && (hasChanged || isLoading) && (React.createElement(Button, { onClick: () => {
                if (comparisonOptions?.dateField && comparisonOptions?.dateField !== dateColumn) {
                    dispatch(homeReducer.updateKPIAnalysisDateColumn(datasetId, comparisonOptions?.dateField));
                }
                const DATE_KEYS = ["currentStart", "currentEnd", "referenceStart", "referenceEnd"];
                DATE_KEYS.forEach((k) => {
                    if (comparisonOptions?.[k] !== KPIDateRange?.[k]) {
                        dispatch(homeReducer.updateKPIDateRangeValue(datasetId, k, comparisonOptions?.[k]));
                    }
                });
                dispatch(homeReducer.updateIsKPIAnalaysis(datasetId, true));
                if (hasChanged) {
                    setIsReindexing(true);
                    dispatch(homeReducer.saveKPISettings(datasetId)).finally(() => {
                        if (setIsKPIAnalysisReady) {
                            setIsKPIAnalysisReady(true);
                        }
                        setHasChangedKPISettings(false);
                        setIsReindexing(false);
                    });
                }
            }, type: "primary", disabled: !hasAllInput, isLoading: isLoading, loadingText: "Evaluating Drivers..." }, "Evaluate Drivers")),
        senseDatasetId && (React.createElement(Tooltip, { overlay: "You must select a a Primary Driver to proceed", trigger: isSaveDisabled ? ["hover"] : [] },
            React.createElement(Button, { type: hasChanged ? "primary" : undefined, disabled: isSaveDisabled, onClick: handleSaveKPISettings }, "Save Settings")))));
};
export default KPIAnalysisSetup;
